import { Check, ArrowRight, Palette, AlertCircle } from "lucide-react";
import { Reveal, SectionHeader } from "../Reveal";
import { PACKAGES, DESIGN_FEE } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";

export const FunnelPricing = ({ onSelectPackage }) => {
    const select = (id) => {
        onSelectPackage?.(id);
        scrollToId("#quote");
    };

    return (
        <section id="pricing" className="relative bg-white text-noir py-24 sm:py-32 overflow-hidden" data-testid="funnel-pricing">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
                <SectionHeader
                    dark={false}
                    eyebrow="CAMPAIGN OPTIONS"
                    title={<>CHOOSE YOUR <span className="text-[#A6A6A6]">CAMPAIGN.</span></>}
                    sub="Four campaign options designed for every stage of your brand — from first spotlight to full market presence."
                />

                <Reveal>
                    <div className="mx-auto mb-12 max-w-3xl rounded-2xl border-2 border-noir bg-noir px-6 py-5 text-center shadow-[0_16px_50px_rgba(8,7,11,0.25)]" data-testid="per-billboard-notice">
                        <p className="flex items-center justify-center gap-2 font-alt text-sm sm:text-base font-extrabold tracking-[0.14em] text-white">
                            <AlertCircle size={18} className="text-hotpink shrink-0" />
                            ALL PRICES ARE PER BILLBOARD / SCREEN.
                        </p>
                        <p className="mt-1 text-xs sm:text-sm text-zinc-400">Multiple billboards / screens are charged individually.</p>
                    </div>
                </Reveal>

                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
                    {PACKAGES.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.08} className="h-full">
                            <article
                                className={`relative flex h-full flex-col rounded-2xl p-7 transition-all duration-300 ${
                                    p.popular
                                        ? "bg-noir text-white border-2 border-hotpink shadow-[0_0_50px_rgba(255,255,255,0.3)] xl:-translate-y-3"
                                        : "bg-white border border-zinc-200 shadow-[0_16px_50px_rgba(8,7,11,0.1)] hover:border-noir hover:-translate-y-1.5"
                                }`}
                                data-testid={`package-card-${p.id}`}
                            >
                                {p.popular && (
                                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-hotpink px-4 py-1 font-alt text-[10px] font-bold tracking-[0.24em] text-noir led-glow">
                                        MOST POPULAR
                                    </span>
                                )}
                                <p className={`font-alt text-[10px] font-bold tracking-[0.28em] ${p.popular ? "text-hotpink" : "text-zinc-500"}`}>{p.duration.toUpperCase()}</p>
                                <h3 className={`mt-2 font-display text-lg font-bold leading-snug ${p.popular ? "text-white" : "text-noir"}`}>{p.name}</h3>
                                <div className="mt-5 flex items-end gap-2">
                                    <span className={`font-display text-5xl font-extrabold ${p.popular ? "text-white" : "text-noir"}`}>${p.price}</span>
                                </div>
                                <p className={`mt-1 font-alt text-[10px] font-bold tracking-[0.24em] ${p.popular ? "text-hotpink" : "text-zinc-500"}`}>PER BILLBOARD</p>
                                <ul className="mt-6 space-y-2.5 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className={`flex items-start gap-2.5 text-[13px] leading-snug ${p.popular ? "text-zinc-300" : "text-zinc-600"}`}>
                                            <Check size={15} className={`mt-0.5 shrink-0 ${p.popular ? "text-hotpink" : "text-noir"}`} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => select(p.id)}
                                    className={`${p.popular ? "btn-glossy-pink" : "btn-outline-dark"} mt-7 w-full !text-[11px]`}
                                    data-testid={`package-select-${p.id}`}
                                >
                                    SELECT THIS OPTION <ArrowRight size={14} />
                                </button>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-12 mx-auto max-w-4xl rounded-2xl border-2 border-noir bg-noir text-white p-7 sm:p-9 shadow-[0_20px_60px_rgba(8,7,11,0.35)]" data-testid="design-addon-card">
                        <div className="flex flex-col md:flex-row md:items-center gap-6">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-hotpink led-glow">
                                <Palette size={24} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <p className="font-alt text-[10px] font-bold tracking-[0.3em] text-hotpink">PREMIUM ADD-ON</p>
                                <h3 className="mt-1.5 font-display text-xl sm:text-2xl font-bold">CUSTOM BILLBOARD DESIGN <span className="text-hotpink">+${DESIGN_FEE}</span> <span className="font-alt text-xs font-bold tracking-[0.2em] text-zinc-400">PER BILLBOARD</span></h3>
                                <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed">
                                    Custom high-quality design, professional advertising artwork, digital billboard-ready
                                    creative, and an eye-catching presentation. Example: 3 billboards with design = 3 × ${DESIGN_FEE} = ${3 * DESIGN_FEE} design fee.
                                </p>
                            </div>
                            <button className="btn-chrome whitespace-nowrap shrink-0" onClick={() => scrollToId("#creative")} data-testid="design-addon-cta">
                                DESIGN DETAILS <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
